import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { watchlistAPI, tmdbAPI } from '../services/api';
import {
  HiOutlineArrowLeft,
  HiCheck,
  HiOutlineChevronDown,
  HiOutlineChevronUp,
} from 'react-icons/hi';
import toast from 'react-hot-toast';
import EpisodeCard from '../components/EpisodeCard';

const TMDB_IMAGE_BASE = 'https://image.tmdb.org/t/p/w300';

function SeriesDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [tvDetails, setTvDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSeasons, setExpandedSeasons] = useState({});
  const [loadingSeasons, setLoadingSeasons] = useState({});

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    setLoading(true);
    try {
      const watchlistRes = await watchlistAPI.getItem(id);
      setItem(watchlistRes.data);

      const tvRes = await tmdbAPI.getTVDetails(watchlistRes.data.tmdb_id);
      setTvDetails(tvRes.data);

      // Initialize seasons if not already set
      if (!watchlistRes.data.seasons || watchlistRes.data.seasons.length === 0) {
        const seasons = tvRes.data.seasons
          ?.filter((s) => s.season_number > 0)
          .map((s) => ({
            season_number: s.season_number,
            watched: false,
            episodes: [],
          }));
        if (seasons && seasons.length > 0) {
          await watchlistAPI.update(id, { seasons });
          setItem((prev) => ({ ...prev, seasons }));
        }
      }
    } catch (error) {
      console.error('Error loading series:', error);
      toast.error(t('seriesDetail.errorLoadingSeries'));
      navigate('/series');
    } finally {
      setLoading(false);
    }
  };

  const loadSeasonEpisodes = async (seasonNumber) => {
    if (loadingSeasons[seasonNumber]) return;

    setLoadingSeasons((prev) => ({ ...prev, [seasonNumber]: true }));
    try {
      const response = await tmdbAPI.getSeasonDetails(item.tmdb_id, seasonNumber);
      const episodes = response.data.episodes?.map((ep) => ({
        episode_number: ep.episode_number,
        name: ep.name || '',
        still_path: ep.still_path || null,
        watched: false,
      })) || [];

      // Check if we already have episode data
      const existingSeason = item.seasons?.find((s) => s.season_number === seasonNumber);
      if (existingSeason?.episodes?.length > 0) {
        // Merge existing watched status
        episodes.forEach((ep) => {
          const existing = existingSeason.episodes.find(
            (e) => e.episode_number === ep.episode_number
          );
          if (existing) {
            ep.watched = existing.watched;
          }
        });
      }

      // Update seasons
      const updatedSeasons = item.seasons?.map((s) =>
        s.season_number === seasonNumber ? { ...s, episodes } : s
      ) || [];

      await watchlistAPI.update(id, { seasons: updatedSeasons });
      setItem((prev) => ({ ...prev, seasons: updatedSeasons }));
    } catch (error) {
      console.error('Error loading season:', error);
      toast.error(t('seriesDetail.errorLoadingSeason'));
    } finally {
      setLoadingSeasons((prev) => ({ ...prev, [seasonNumber]: false }));
    }
  };

  const toggleSeason = async (seasonNumber) => {
    const isExpanded = expandedSeasons[seasonNumber];

    if (!isExpanded) {
      // Load episodes if not loaded
      const season = item.seasons?.find((s) => s.season_number === seasonNumber);
      if (!season?.episodes?.length) {
        await loadSeasonEpisodes(seasonNumber);
      }
    }

    setExpandedSeasons((prev) => ({
      ...prev,
      [seasonNumber]: !prev[seasonNumber],
    }));
  };

  const toggleSeasonWatched = async (seasonNumber) => {
    const season = item.seasons?.find((s) => s.season_number === seasonNumber);
    if (!season) return;

    const newWatched = !season.watched;
    const updatedSeasons = item.seasons?.map((s) =>
      s.season_number === seasonNumber
        ? {
            ...s,
            watched: newWatched,
            episodes: s.episodes?.map((ep) => ({ ...ep, watched: newWatched })) || [],
          }
        : s
    );

    try {
      await watchlistAPI.update(id, { seasons: updatedSeasons });
      setItem((prev) => ({ ...prev, seasons: updatedSeasons }));
      toast.success(newWatched ? t('seriesDetail.seasonMarkedAsWatched') : t('seriesDetail.seasonUnmarked'));
    } catch (error) {
      toast.error(t('seriesDetail.errorUpdating'));
    }
  };

  const toggleEpisodeWatched = async (seasonNumber, episodeNumber) => {
    const season = item.seasons?.find((s) => s.season_number === seasonNumber);
    if (!season) return;

    const episode = season.episodes?.find((e) => e.episode_number === episodeNumber);
    if (!episode) return;

    const newWatched = !episode.watched;
    const updatedEpisodes = season.episodes?.map((ep) =>
      ep.episode_number === episodeNumber ? { ...ep, watched: newWatched } : ep
    );

    // Check if all episodes are watched
    const allWatched = updatedEpisodes?.every((ep) => ep.watched);

    const updatedSeasons = item.seasons?.map((s) =>
      s.season_number === seasonNumber
        ? { ...s, watched: allWatched, episodes: updatedEpisodes }
        : s
    );

    try {
      await watchlistAPI.update(id, { seasons: updatedSeasons });
      setItem((prev) => ({ ...prev, seasons: updatedSeasons }));
    } catch (error) {
      toast.error(t('seriesDetail.errorUpdating'));
    }
  };

  const getSeasonProgress = (season) => {
    if (season.watched) return 100;
    if (!season.episodes?.length) return 0;
    const watched = season.episodes.filter((ep) => ep.watched).length;
    return Math.round((watched / season.episodes.length) * 100);
  };

  const getTotalProgress = () => {
    if (!item?.seasons?.length) return 0;
    let total = 0;
    let watched = 0;
    item.seasons.forEach((s) => {
      if (s.watched) {
        total += s.episodes?.length || 1;
        watched += s.episodes?.length || 1;
      } else if (s.episodes?.length) {
        total += s.episodes.length;
        watched += s.episodes.filter((ep) => ep.watched).length;
      }
    });
    return total > 0 ? Math.round((watched / total) * 100) : 0;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <button
        className="btn btn-secondary"
        onClick={() => navigate('/series')}
        style={{ marginBottom: '24px' }}
      >
        <HiOutlineArrowLeft />
        {t('common.back')}
      </button>

      {/* Header */}
      <div
        style={{
          display: 'flex',
          gap: '24px',
          marginBottom: '32px',
          flexWrap: 'wrap',
        }}
      >
        <div
          style={{
            width: '200px',
            flexShrink: 0,
            borderRadius: 'var(--radius-lg)',
            overflow: 'hidden',
            boxShadow: 'var(--shadow-lg)',
          }}
        >
          {item?.poster_path ? (
            <img
              src={`${TMDB_IMAGE_BASE}${item.poster_path}`}
              alt={item.title}
              style={{ width: '100%', display: 'block' }}
            />
          ) : (
            <div
              style={{
                width: '100%',
                aspectRatio: '2/3',
                background: '#e5e7eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {t('media.noImage')}
            </div>
          )}
        </div>

        <div style={{ flex: 1, minWidth: '280px' }}>
          <h1 style={{ fontSize: '28px', marginBottom: '8px' }}>{item?.title}</h1>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
            {tvDetails?.number_of_seasons} {t('series.seasons').toLowerCase()} •{' '}
            {tvDetails?.number_of_episodes} {t('series.episodes').toLowerCase()}
          </p>

          {/* Progress */}
          <div style={{ marginBottom: '20px' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '8px',
                fontSize: '14px',
              }}
            >
              <span style={{ fontWeight: '600' }}>{t('seriesDetail.totalProgress')}</span>
              <span style={{ color: 'var(--text-secondary)' }}>
                {getTotalProgress()}%
              </span>
            </div>
            <div
              style={{
                height: '8px',
                background: 'var(--border)',
                borderRadius: '4px',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${getTotalProgress()}%`,
                  background:
                    getTotalProgress() === 100
                      ? 'var(--success)'
                      : 'linear-gradient(90deg, var(--primary), var(--secondary))',
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
          </div>

          {tvDetails?.overview && (
            <p
              style={{
                fontSize: '14px',
                color: 'var(--text-secondary)',
                lineHeight: '1.7',
              }}
            >
              {tvDetails.overview}
            </p>
          )}
        </div>
      </div>

      {/* Seasons */}
      <div className="card">
        <div className="card-header">
          <h2>{t('series.seasons')}</h2>
        </div>

        <div className="season-tracker">
          {item?.seasons?.map((season) => {
            const progress = getSeasonProgress(season);
            const isExpanded = expandedSeasons[season.season_number];
            const isLoading = loadingSeasons[season.season_number];

            return (
              <div key={season.season_number} style={{ marginBottom: '8px' }}>
                <div
                  className="season-header"
                  onClick={() => toggleSeason(season.season_number)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <button
                      className={`btn ${season.watched ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSeasonWatched(season.season_number);
                      }}
                      style={{ padding: '8px 12px' }}
                    >
                      <HiCheck />
                    </button>
                    <div>
                      <h4>{t('series.season')} {season.season_number}</h4>
                      <span
                        style={{
                          fontSize: '12px',
                          color: 'var(--text-muted)',
                        }}
                      >
                        {progress}% {t('episodes.watched').toLowerCase()}
                      </span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div
                      style={{
                        width: '100px',
                        height: '4px',
                        background: 'var(--border)',
                        borderRadius: '2px',
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          height: '100%',
                          width: `${progress}%`,
                          background:
                            progress === 100 ? 'var(--success)' : 'var(--primary)',
                        }}
                      />
                    </div>
                    {isExpanded ? (
                      <HiOutlineChevronUp size={20} />
                    ) : (
                      <HiOutlineChevronDown size={20} />
                    )}
                  </div>
                </div>

                {isExpanded && (
                  <div style={{ padding: '16px', background: 'var(--background)' }}>
                    {isLoading ? (
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          padding: '20px',
                        }}
                      >
                        <div className="spinner" style={{ width: '24px', height: '24px' }} />
                      </div>
                    ) : season.episodes?.length > 0 ? (
                      <div className="episodes-grid">
                        {season.episodes.map((ep) => (
                          <EpisodeCard
                            key={ep.episode_number}
                            episode={ep}
                            seasonNumber={season.season_number}
                            onToggleWatched={() =>
                              toggleEpisodeWatched(season.season_number, ep.episode_number)
                            }
                          />
                        ))}
                      </div>
                    ) : (
                      <p
                        style={{
                          textAlign: 'center',
                          color: 'var(--text-muted)',
                          padding: '20px',
                        }}
                      >
                        {t('seriesDetail.noEpisodesAvailable')}
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default SeriesDetail;
